import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { isCancellableRunStatus, type EnrichedApproval, type Run, type RunClarification, type RunEvidence, type RunStageRecord } from '@orqforge/shared';
import { apiClient, type ApDemoOverview, type RunDetailSnapshot, type RunOutputFile, type WorkLog, type WorkLogDelta, type WorkLogEntry } from '../lib/api-client';
import { sseUrl } from '../lib/sse';
import { useApp } from '../contexts/AppContext';
import { ApCasePack } from '../components/ap/ApCasePack';
import { applyPendingApprovals, buildRunRequestGroups, findResumableRun, nextActionForStatus, orderRequestRuns, rollupStatus, type RunRequestGroup } from '../components/runs/run-requests';
import { buildStageView } from '../components/runs/RunProgress';
import { friendlyWorkLogToolTitle } from '../components/collaborate/tool-presentation';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { MarkdownPreview } from '../components/ui/MarkdownPreview';

const STORAGE_KEY = 'orqforge.runs.selectedId';

function badgeVariant(status: string): 'success' | 'destructive' | 'warning' | 'secondary' | 'default' {
  if (status === 'completed') return 'success';
  if (status === 'failed' || status === 'escalated' || status === 'changes_requested') return 'destructive';
  if (status === 'waiting_approval' || status === 'waiting_review' || status === 'waiting_input') return 'warning';
  if (status === 'cancelled') return 'secondary';
  return 'default';
}

function canResume(status: string): boolean {
  return ['failed', 'cancelled', 'escalated'].includes(status);
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatInput(raw: string): string {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const text = typeof parsed.text === 'string' ? parsed.text : typeof parsed.subject === 'string' ? parsed.subject : '';
    return text ? text.slice(0, 80) : raw.slice(0, 100);
  } catch {
    return raw.length > 100 ? `${raw.slice(0, 97)}...` : raw;
  }
}

function mergeCached(cached: RunRequestGroup, run: Run): RunRequestGroup {
  let updatedRuns: Run[];
  let rootRun: Run;
  if (cached.id === run.id || cached.runs.some((row) => row.id === run.id)) {
    updatedRuns = cached.runs.map((row) => (row.id === run.id ? run : row));
    rootRun = cached.id === run.id ? run : cached.rootRun;
  } else {
    const cachedIds = new Set(cached.runs.map((row) => row.id));
    if (!cachedIds.has(run.parentRunId ?? '') && !cachedIds.has(run.resumedFromRunId ?? '')) return cached;
    updatedRuns = [...cached.runs, run];
    rootRun = cached.rootRun;
  }
  const orderedRuns = orderRequestRuns(rootRun, updatedRuns);
  const status = rollupStatus(orderedRuns, rootRun);
  return {
    ...cached,
    rootRun,
    runs: orderedRuns,
    relatedRunCount: orderedRuns.length,
    status,
    currentStage: orderedRuns.find((row) => ['waiting_input', 'waiting_review', 'waiting_approval', 'running', 'queued'].includes(row.status))?.currentStage ?? rootRun.currentStage ?? null,
    nextAction: nextActionForStatus(status, orderedRuns, rootRun),
    resumeRun: findResumableRun(orderedRuns, rootRun),
  };
}

export function RunsPage() {
  const { workspaceId } = useApp();
  const [params] = useSearchParams();
  const runIdParam = params.get('runId');
  const [runs, setRuns] = useState<Run[]>([]);
  const [approvals, setApprovals] = useState<EnrichedApproval[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [cached, setCached] = useState<RunRequestGroup | null>(null);
  const [stages, setStages] = useState<RunStageRecord[]>([]);
  const [clarifications, setClarifications] = useState<RunClarification[]>([]);
  const [files, setFiles] = useState<RunOutputFile[]>([]);
  const [workLog, setWorkLog] = useState<WorkLog | null>(null);
  const [agentFilter, setAgentFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [evidence, setEvidence] = useState<RunEvidence | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ id: string; force: boolean } | null>(null);
  const [resumeTarget, setResumeTarget] = useState<string | null>(null);
  const [resumeInstructions, setResumeInstructions] = useState('');
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  const [overview, setOverview] = useState<ApDemoOverview | null>(null);

  const groups = useMemo(() => applyPendingApprovals(buildRunRequestGroups(runs), approvals, runs), [runs, approvals]);
  const filtered = statusFilter === 'running' ? groups.filter((group) => group.status === 'running' || group.status === 'queued') : statusFilter ? groups.filter((group) => group.status === statusFilter) : groups;
  const selectedRequest = cached ?? groups.find((group) => group.id === selectedId || group.runs.some((run) => run.id === selectedId)) ?? null;
  const selectedRun = selectedRequest ? (selectedId && selectedRequest.runs.find((run) => run.id === selectedId)) || selectedRequest.rootRun : null;
  const stageView = buildStageView(stages);
  const openClarifications = clarifications.filter((row) => row.status === 'open');
  const agents = [...new Set((workLog?.entries ?? []).map((entry) => entry.agentName).filter(Boolean))] as string[];
  const logEntries = (workLog?.entries ?? []).filter((entry) => !agentFilter || entry.agentName === agentFilter);
  const apNeed = (overview?.cases ?? []).find((row) => row.status === 'AWAITING_APPROVAL' || row.status === 'EXCEPTION') ?? (overview?.cases ?? []).find((row) => row.status === 'IN_PAYMENT_PROPOSAL') ?? overview?.cases[0] ?? null;

  function upsertRun(run: Run) {
    setRuns((current) => {
      const index = current.findIndex((row) => row.id === run.id);
      if (index === -1) return [run, ...current];
      const next = [...current];
      next[index] = run;
      return next;
    });
    setCached((current) => (current ? mergeCached(current, run) : current));
    if (run.status === 'waiting_approval') void apiClient.listApprovals().then(setApprovals).catch(() => setApprovals([]));
  }

  function applySnapshot(snapshot: RunDetailSnapshot) {
    upsertRun(snapshot.run);
    setStages(snapshot.stages);
    setClarifications(snapshot.clarifications);
    setFiles(snapshot.files);
  }

  function select(run: Run, request?: RunRequestGroup) {
    if (selectedId === run.id) return;
    setSelectedId(run.id);
    sessionStorage.setItem(STORAGE_KEY, run.id);
    setShowFullPrompt(false);
    setWorkLog(null);
    setAgentFilter(null);
    setShowEvidence(false);
    setEvidence(null);
    setCached(request ?? buildRunRequestGroups(runs).find((group) => group.id === run.id || group.runs.some((row) => row.id === run.id)) ?? null);
  }

  useEffect(() => {
    void apiClient.getApDemoOverview().then(setOverview).catch(() => setOverview(null));
    void apiClient.listRuns().then((rows) => {
      setRuns(rows);
      const deep = runIdParam;
      const saved = deep ? null : sessionStorage.getItem(STORAGE_KEY);
      const target = rows.find((run) => run.id === (deep || saved));
      if (target) select(target);
    }).catch(() => setRuns([]));
    void apiClient.listApprovals().then(setApprovals).catch(() => setApprovals([]));
  }, [workspaceId, runIdParam]);

  useEffect(() => {
    if (typeof EventSource === 'undefined') return;
    let source: EventSource | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let closed = false;
    const open = async () => {
      source?.close();
      const url = await sseUrl('/api/runs/events', { workspaceId });
      if (closed) return;
      source = new EventSource(url);
      source.addEventListener('run-deltas', (event) => {
        try {
          const payload = JSON.parse((event as MessageEvent).data) as { runs: Run[] };
          for (const run of payload.runs) upsertRun(run);
        } catch {
          // Ignore malformed stream payloads.
        }
      });
      source.onerror = () => {
        source?.close();
        timer = setTimeout(() => void open(), 3000);
      };
    };
    void open();
    return () => {
      closed = true;
      source?.close();
      if (timer) clearTimeout(timer);
    };
  }, [workspaceId]);

  useEffect(() => {
    if (!selectedId || typeof EventSource === 'undefined') return;
    const id = selectedId;
    let detail: EventSource | undefined;
    let work: EventSource | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let closed = false;
    void apiClient.getRunDetailSnapshot(id).then((snapshot) => {
      if (!closed) applySnapshot(snapshot);
    }).catch(() => undefined);
    void apiClient.getRunWorkLog(id).then((log) => {
      if (!closed) setWorkLog(log);
    }).catch(() => undefined);
    const open = async () => {
      detail?.close();
      work?.close();
      const detailUrl = await sseUrl(`/api/runs/${id}/events`);
      const workUrl = await sseUrl(`/api/runs/${id}/work-log/events`);
      if (closed) return;
      detail = new EventSource(detailUrl);
      detail.addEventListener('run-detail', (event) => {
        try {
          applySnapshot(JSON.parse((event as MessageEvent).data) as RunDetailSnapshot);
        } catch {
          // Ignore malformed stream payloads.
        }
      });
      work = new EventSource(workUrl);
      work.addEventListener('work-log-delta', (event) => {
        try {
          const delta = JSON.parse((event as MessageEvent).data) as WorkLogDelta;
          setWorkLog((current) => {
            if (!current) return { runId: delta.runId, generatedAt: new Date().toISOString(), entries: delta.entries };
            const existing = new Set(current.entries.map((entry) => entry.id));
            const fresh = delta.entries.filter((entry) => !existing.has(entry.id));
            if (!fresh.length) return current;
            return { ...current, generatedAt: new Date().toISOString(), entries: [...fresh, ...current.entries] };
          });
        } catch {
          // Ignore malformed stream payloads.
        }
      });
      const retry = () => {
        detail?.close();
        work?.close();
        timer = setTimeout(() => void open(), 3000);
      };
      detail.onerror = retry;
      work.onerror = retry;
    };
    void open();
    return () => {
      closed = true;
      detail?.close();
      work?.close();
      if (timer) clearTimeout(timer);
    };
  }, [selectedId]);

  async function stop(id: string, force: boolean) {
    setCancellingId(id);
    try {
      const run = force ? await apiClient.forceCancelRun(id) : await apiClient.cancelRun(id);
      upsertRun(run);
    } finally {
      setCancellingId(null);
      setConfirm(null);
    }
  }

  async function submitResume() {
    if (!resumeTarget || resuming) return;
    setResuming(true);
    setResumeError(null);
    try {
      const run = await apiClient.resumeRun(resumeTarget, resumeInstructions);
      upsertRun(run);
      setResumeTarget(null);
      setResumeInstructions('');
      select(run);
    } catch (err) {
      setResumeError(err instanceof Error ? err.message : 'Failed to resume run');
    } finally {
      setResuming(false);
    }
  }

  return (
    <div>
      <h1 className="mb-2 text-2xl font-semibold">Runs</h1>
      <p className="text-sm text-gray-500">Requests grouped by the initial user prompt, with related agent runs, next steps, and outputs in one place.</p>
      {apNeed && (
        <div className="mt-4">
          <ApCasePack caseRow={apNeed} audit={(overview?.audit ?? []).filter((entry) => entry.case_id === apNeed.case_id)} />
          <div className="mt-2 flex gap-2">
            <Link to="/mailbox"><Button size="sm" variant="ghost">Mailbox</Button></Link>
            <Link to={`/payments?caseId=${apNeed.case_id}`}><Button size="sm" variant="ghost">Payment order</Button></Link>
          </div>
        </div>
      )}
      {selectedRequest && selectedRun && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="text-xs text-gray-500">Request</span>
              <div className="flex items-center gap-3">
                <div className="text-lg font-semibold">{selectedRequest.title}</div>
                <Button size="sm" variant="ghost" onClick={() => setShowFullPrompt((value) => !value)}>{showFullPrompt ? 'Hide' : 'Show'} full prompt</Button>
              </div>
              {showFullPrompt && <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded border border-gray-200 p-3 font-mono text-xs">{selectedRun.input}</pre>}
              <div className="mt-1 text-xs text-gray-500">root run <code>{selectedRun.id.slice(0, 8)}</code> · {selectedRequest.relatedRunCount} related {selectedRequest.relatedRunCount === 1 ? 'run' : 'runs'}{selectedRequest.currentStage ? ` · stage: ${selectedRequest.currentStage}` : ''}</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={badgeVariant(selectedRequest.status)}>{selectedRequest.status}</Badge>
              {canResume(selectedRun.status) && <Button size="sm" variant="ghost" disabled={resuming} onClick={() => { setResumeTarget(selectedRun.id); setResumeInstructions(''); }}>Resume</Button>}
              {isCancellableRunStatus(selectedRun.status) && <Button size="sm" variant="ghost" className="text-red-600" disabled={cancellingId === selectedRun.id} onClick={() => setConfirm({ id: selectedRun.id, force: Boolean(selectedRun.cancelRequestedAt) })}>{selectedRun.cancelRequestedAt ? 'Force Stop' : 'Stop'}</Button>}
              <Button size="sm" variant="ghost" onClick={() => {
                const next = !showEvidence;
                setShowEvidence(next);
                if (next && selectedId) void apiClient.getRunEvidence(selectedId).then(setEvidence);
              }}>Evidence</Button>
              <Button size="sm" variant="ghost" onClick={() => { setSelectedId(null); setCached(null); sessionStorage.removeItem(STORAGE_KEY); }}>Close</Button>
            </div>
          </div>
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/30">
            <div className="text-xs font-semibold uppercase tracking-wide">{selectedRequest.nextAction.label}</div>
            <p className="mt-1 text-sm">{selectedRequest.nextAction.detail}</p>
            <div className="mt-2 flex gap-2">
              {selectedRequest.nextAction.kind === 'review' && <Link to="/review"><Button size="sm">Open review</Button></Link>}
              {selectedRequest.nextAction.kind === 'approval' && <Link to="/approvals"><Button size="sm">Open approvals</Button></Link>}
              {(selectedRequest.nextAction.kind === 'failed' || selectedRequest.nextAction.kind === 'resume') && <Button size="sm" disabled={!canResume(selectedRequest.resumeRun.status)} onClick={() => setResumeTarget(selectedRequest.resumeRun.id)}>Resume</Button>}
            </div>
          </div>
          {selectedRun.error && <pre className="mt-3 whitespace-pre-wrap rounded border border-red-300 bg-red-50 p-3 text-xs text-red-700">{selectedRun.error}</pre>}
          <div className="mt-3 flex items-stretch gap-1 overflow-x-auto pb-2">
            {stageView.map((stage) => (
              <div key={stage.stage} className="min-w-[92px] rounded-lg border border-gray-200 px-3 py-2 text-center text-xs dark:border-gray-700">
                <div className="font-semibold">{stage.stage}</div>
                <div className="text-[10px] text-gray-400">{stage.status === 'done' ? `${formatMs(stage.elapsedMs)} · ${stage.tokens}t` : stage.status === 'active' ? 'running...' : stage.status}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <div className="text-xs uppercase tracking-wide text-gray-500">Work Log</div>
            {agents.length > 1 && (
              <div className="mt-2 flex flex-wrap gap-1">
                <Button size="sm" variant={!agentFilter ? 'default' : 'outline'} onClick={() => setAgentFilter(null)}>All agents</Button>
                {agents.map((agent) => <Button key={agent} size="sm" variant={agentFilter === agent ? 'default' : 'outline'} onClick={() => setAgentFilter(agentFilter === agent ? null : agent)}>{agent}</Button>)}
              </div>
            )}
            <div className="mt-3 h-80 overflow-auto" data-work-log-live>
              {logEntries.length === 0 && <p className="text-xs text-gray-400">{agentFilter ? `No events for ${agentFilter}.` : 'No work log events recorded yet.'}</p>}
              {logEntries.map((entry: WorkLogEntry) => (
                <div key={entry.id} className="mb-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                  <div className="flex justify-between gap-2">
                    <span className="text-sm font-semibold">{friendlyWorkLogToolTitle(entry.title, entry.detail)}</span>
                    <span className="text-[11px] text-gray-400">{entry.occurredAt}</span>
                  </div>
                  <div className="mt-1 text-sm"><MarkdownPreview content={entry.detail} /></div>
                  <div className="mt-2 text-[11px] text-gray-400">{entry.runId.slice(0, 8)}{entry.agentName ? ` · ${entry.agentName}` : ''}{entry.status ? ` · ${entry.status}` : ''}</div>
                </div>
              ))}
            </div>
          </div>
          {openClarifications.length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
              <span className="text-xs uppercase tracking-wide text-amber-700">Needs input</span>
              {openClarifications.map((row) => (
                <div key={row.id} className="mt-2 rounded bg-white p-3 dark:bg-gray-900">
                  <MarkdownPreview content={row.question} />
                  <textarea className="mt-2 min-h-24 w-full rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800" placeholder="Type an answer to resume the run..." value={answers[row.id] ?? ''} onChange={(event) => setAnswers((current) => ({ ...current, [row.id]: event.target.value }))} />
                  <div className="mt-2 flex justify-end">
                    <Button size="sm" disabled={!(answers[row.id] ?? '').trim()} onClick={() => {
                      const answer = answers[row.id]?.trim();
                      if (!answer) return;
                      void apiClient.answerClarification(row.runId, row.id, answer).then(() => setAnswers((current) => ({ ...current, [row.id]: '' })));
                    }}>Answer</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div id="pipeline-outputs" className="mt-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <div className="text-xs uppercase tracking-wide text-gray-500">Current output</div>
            {files.length === 0 && !selectedRun.output && <p className="mt-2 text-xs text-gray-400">No output files yet.</p>}
            {selectedRun.output && files.length === 0 && <MarkdownPreview content={selectedRun.output} />}
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {files.map((file) => (
                <div key={file.uploadId ?? file.filename} className="rounded border border-gray-200 p-3 text-sm dark:border-gray-700">
                  <div className="truncate font-medium">{file.filename}</div>
                  {file.url && <a className="text-xs text-blue-600" href={file.url} target="_blank" rel="noreferrer">Download</a>}
                </div>
              ))}
            </div>
          </div>
          {showEvidence && evidence && (
            <div className="mt-3 rounded-lg border border-gray-200 p-3 text-xs dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="uppercase tracking-wide text-gray-500">Evidence pack</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => {
                    const blob = new Blob([JSON.stringify(evidence, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const anchor = document.createElement('a');
                    anchor.href = url;
                    anchor.download = `evidence-${evidence.run.id.slice(0, 8)}.json`;
                    anchor.click();
                    URL.revokeObjectURL(url);
                  }}>JSON</Button>
                  <Button size="sm" variant="outline" onClick={() => {
                    if (!selectedId) return;
                    void apiClient.exportRunEvidencePdf(selectedId).then((blob) => {
                      const url = URL.createObjectURL(blob);
                      const anchor = document.createElement('a');
                      anchor.href = url;
                      anchor.download = `evidence-${selectedId.slice(0, 8)}.pdf`;
                      anchor.click();
                      URL.revokeObjectURL(url);
                    });
                  }}>PDF</Button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                <Badge variant="outline">{evidence.counts.stages} stages</Badge>
                <Badge variant="outline">{evidence.counts.steps} steps</Badge>
                <Badge variant="outline">{evidence.counts.delegations} delegations</Badge>
                <Badge variant="outline">{evidence.counts.reviewGates} reviews</Badge>
                <Badge variant="outline">{evidence.counts.clarifications} clarifications</Badge>
                <Badge variant="outline">{evidence.counts.audit} audit</Badge>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="mb-3 mt-4 flex flex-wrap gap-1">
        {['all', 'completed', 'failed', 'waiting_approval', 'waiting_input', 'running'].map((status) => (
          <Button key={status} size="sm" variant={(status === 'all' ? !statusFilter : statusFilter === status) ? 'default' : 'outline'} onClick={() => setStatusFilter(status === 'all' || statusFilter === status ? null : status)}>
            {status === 'all' ? 'All' : status === 'waiting_approval' ? 'Needs approval' : status === 'waiting_input' ? 'Needs input' : status[0].toUpperCase() + status.slice(1)}
          </Button>
        ))}
      </div>
      <table className="w-full text-left text-sm">
        <thead><tr className="text-xs uppercase text-gray-400"><th>Request</th><th>Next step</th><th>Runs</th><th>Stage</th><th>Created</th></tr></thead>
        <tbody>
          {filtered.map((request) => (
            <tr key={request.id} className={`cursor-pointer border-t border-gray-100 dark:border-gray-800 ${selectedRequest?.id === request.id ? 'bg-gray-50 dark:bg-gray-800' : ''}`} onClick={() => select(request.rootRun, request)}>
              <td className="py-2"><div className="font-medium">{request.title}</div><div className="text-xs text-gray-500">root {request.rootRun.id.slice(0, 8)}</div></td>
              <td><Badge variant={badgeVariant(request.status)}>{request.nextAction.label}</Badge></td>
              <td>{request.relatedRunCount}</td>
              <td>{request.currentStage || '-'}</td>
              <td className="text-gray-500">{String(request.rootRun.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <ConfirmDialog
        open={Boolean(confirm)}
        message={confirm?.force ? 'The run will be terminated immediately. In-progress work may be lost.' : 'It will finish its current step, then stop gracefully.'}
        confirmText={confirm?.force ? 'Force Stop' : 'Stop'}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { if (confirm) void stop(confirm.id, confirm.force); }}
      />
      {resumeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
            <h2 className="text-lg font-semibold">Resume run</h2>
            <p className="mt-1 text-sm text-gray-500">Add instructions for a new attempt. Use this for failed, cancelled, or escalated runs.</p>
            <textarea className="mt-3 min-h-40 w-full rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800" placeholder="Example: Continue with these corrected details..." value={resumeInstructions} onChange={(event) => setResumeInstructions(event.target.value)} />
            {resumeError && <p className="mt-2 text-sm text-red-600">{resumeError}</p>}
            <div className="mt-3 flex justify-end gap-2">
              <Button variant="ghost" size="sm" disabled={resuming} onClick={() => setResumeTarget(null)}>Cancel</Button>
              <Button size="sm" disabled={resuming} onClick={() => void submitResume()}>{resuming ? 'Resuming...' : 'Start resumed run'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
